import { useCallback, useMemo, useState } from "react";

const defaultGetId = (item) => item.id;

const keepAvailable = (selectedIds, availableIds) => {
    const kept = new Set();
    selectedIds.forEach((id) => {
        if (availableIds.has(id)) kept.add(id);
    });

    return kept.size === selectedIds.size ? selectedIds : kept;
};

/**
 * Держит выбор в Set и синхронизируется со списком прямо в рендере, без эффекта:
 * список и галочки появляются в одном кадре.
 *
 * Первый непустой список выбирается целиком, каждый следующий (рефетч профиля,
 * повторная загрузка предприятий) сохраняет уже сделанный выбор и выбрасывает
 * только те id, которых в новом списке больше нет.
 */
export const useMultiSelect = (items, { getId = defaultGetId, selectAllOnFirstLoad = true } = {}) => {
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [syncedKey, setSyncedKey] = useState(null);

    // Ключ по составу списка, а не по ссылке: немемоизированный селектор отдаёт
    // новый массив на каждый рендер, и сверка по ссылке зациклила бы синхронизацию.
    const ids = items.map(getId);
    const key = ids.join("\u0000");

    if (key !== syncedKey) {
        if (ids.length > 0) {
            setSyncedKey(key);
            setSelectedIds(
                syncedKey === null
                    ? (selectAllOnFirstLoad ? new Set(ids) : selectedIds)
                    : keepAvailable(selectedIds, new Set(ids)),
            );
        } else if (syncedKey !== null) {
            setSyncedKey(key);
            setSelectedIds(keepAvailable(selectedIds, new Set()));
        }
    }

    const toggle = useCallback((id, next) => {
        setSelectedIds((prev) => {
            const shouldSelect = next === undefined ? !prev.has(id) : next;
            if (shouldSelect === prev.has(id)) return prev;

            const updated = new Set(prev);
            if (shouldSelect) updated.add(id);
            else updated.delete(id);
            return updated;
        });
    }, []);

    const toggleMany = useCallback((ids) => {
        if (ids.length === 0) return;

        setSelectedIds((prev) => {
            const allSelected = ids.every((id) => prev.has(id));
            const updated = new Set(prev);
            ids.forEach((id) => {
                if (allSelected) updated.delete(id);
                else updated.add(id);
            });
            return updated;
        });
    }, []);

    const selectedIdList = useMemo(() => Array.from(selectedIds), [selectedIds]);

    return { selectedIds, selectedIdList, toggle, toggleMany };
};
